import { useTranslation } from 'next-i18next'
import { createValueTypeGuard } from 'services/create-value-type-guard'

import { FeatureRadioTile, type FeatureRadioTileProps } from './feature-radio-tile'
import { FeatureRadioTileGroup } from './feature-radio-tile-group'

type RadioTile<Value extends string> = {
    titleKey: Value
    bodyKey: `${Value}Body` | Value
    icon: React.ReactElement
    scene: React.ReactNode
    sceneName?: Value
    copySpacing?: boolean
}

type FeatureRadioGroupProps<Value extends string> = {
    onChange: (value: Value) => void
    value: Value
    className?: string
    variant?: FeatureRadioTileProps['variant']
    radioTiles: RadioTile<Value>[]
    namespace?: string
}

export function FeatureRadioGroup<Value extends string>({
    onChange,
    value,
    radioTiles,
    className,
    variant,
    namespace,
}: FeatureRadioGroupProps<Value>) {
    const { t } = useTranslation(namespace)

    function onChangeHandler(value: string) {
        const typeGuard = createValueTypeGuard<Value>(
            radioTiles.map(({ sceneName, titleKey }) => sceneName || titleKey),
        )
        if (typeGuard(value)) onChange(value)
    }

    return (
        <FeatureRadioTileGroup onChange={onChangeHandler} value={value} className={className}>
            {radioTiles.map(({ titleKey, bodyKey, icon, scene, sceneName, copySpacing }) => {
                const { title, body } = namespace
                    ? { title: t(titleKey), body: t(bodyKey) }
                    : { title: titleKey, body: bodyKey }

                return (
                    <FeatureRadioTile
                        key={titleKey}
                        title={title}
                        icon={icon}
                        value={sceneName || titleKey}
                        variant={variant}
                        scene={scene}
                        copySpacing={copySpacing}
                    >
                        {body}
                    </FeatureRadioTile>
                )
            })}
        </FeatureRadioTileGroup>
    )
}
